// Who owns each item needed from Amazon, and what Amazon has said on it: the owner chip and
// its picker, Mark as received, the block showing an answer waiting on InRange, and the
// item's history (docs/Y2026-Q4/design-amazon-item-owners.md sections 2.3-2.7). Owners load
// once for every page of the overview, through OwnersProvider.
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  answerShows,
  approvedResolved,
  type Assignee,
  waitsOnInRange,
  INRANGE,
  type ItemAnswer,
  itemId,
  type OverviewPerson,
  type Owners,
  useAmazonOwners,
  useAssignItem,
  useDismissAnswer,
  useItemHistory,
  useMarkResolved,
  useReplyToInRange,
} from "../../api/hooks/useAmazonOwners";
import { UserContext } from "../../auth";
import {
  AssigneeChip,
  AssigneeMenu,
  PersonMark as SharedMark,
} from "../../components/AssigneePicker";
import { CitedBody } from "../../components/CitedBody";
import { NoteDialog } from "../../components/NoteDialog";
import { useCitations } from "../../components/useCitations";
import { useReadOnly } from "./ViewAs";
import styles from "./itemOwners.module.css";

/** An item on one building, as the owner controls act on it. */
export interface ItemRef {
  projectId: string;
  itemKey: string;
  text: string;
  siteCode: string;
  /** Still owed by Amazon: not received, or only partly. */
  owed: boolean;
}

type Dialog =
  | { kind: "clear"; item: ItemRef; expected: Assignee }
  | { kind: "inrange"; item: ItemRef; expected: Assignee }
  | { kind: "resolved"; item: ItemRef }
  | { kind: "reply"; item: ItemRef; answerId: string };

interface OwnersValue {
  owners: Owners | undefined;
  failed: boolean;
  isStaff: boolean;
  /** The items each person owns that are still owed, by person id ("inrange" too). */
  openCounts: Map<string, number>;
  ownerOf: (item: ItemRef) => Assignee;
  answerOf: (item: ItemRef) => ItemAnswer | undefined;
  person: (id: Assignee) => OverviewPerson | undefined;
  nameOf: (id: Assignee) => string;
  openHistory: (item: ItemRef) => void;
  ask: (dialog: Dialog) => void;
}

const OwnersContext = createContext<OwnersValue | null>(null);

export const useOwners = (): OwnersValue => {
  const value = useContext(OwnersContext);
  if (!value) throw new Error("useOwners outside OwnersProvider");
  return value;
};

/** The round badge a person carries on the page: their initials, InRange's, "+" for
 * nobody, "All" for everyone. The owner chip, the owner menu and the person filter use it. */
export const PersonMark = ({ id, name }: { id: Assignee | "everyone"; name: string }) => (
  <SharedMark
    name={name}
    tone={id === INRANGE ? "org" : id === "everyone" ? "everyone" : id ? "person" : "nobody"}
    text={id === INRANGE ? "IR" : undefined}
  />
);

export const OwnersProvider = ({
  owedItems,
  children,
}: {
  /** The ids (``itemId``) of the items still owed on the buildings shown. */
  owedItems: Set<string>;
  children: ReactNode;
}) => {
  const { role } = useContext(UserContext);
  const isStaff = role === "admin";
  const query = useAmazonOwners();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [history, setHistory] = useState<ItemRef | null>(null);
  const data = query.data;

  const value = useMemo((): OwnersValue => {
    const owner = new Map((data?.owners ?? []).map((o) => [itemId(o.project_id, o.item_key), o.assignee]));
    const answers = new Map((data?.answers ?? []).map((a) => [itemId(a.project_id, a.item_key), a]));
    const people = new Map([...(data?.people ?? []), ...(data?.former ?? [])].map((p) => [p.person_id, p]));
    const openCounts = new Map<string, number>();
    for (const [id, assignee] of owner)
      if (assignee && owedItems.has(id)) openCounts.set(assignee, (openCounts.get(assignee) ?? 0) + 1);
    const nameOf = (id: Assignee): string => {
      if (id === null) return "Nobody";
      if (id === INRANGE) return "InRange";
      const p = people.get(id);
      return p ? `${p.name}${p.no_access ? " (no access)" : ""}` : "Someone no longer listed";
    };
    return {
      owners: data,
      failed: query.isError,
      isStaff,
      openCounts,
      ownerOf: (item) => owner.get(itemId(item.projectId, item.itemKey)) ?? null,
      answerOf: (item) => answers.get(itemId(item.projectId, item.itemKey)),
      person: (id) => (id ? people.get(id) : undefined),
      nameOf,
      openHistory: setHistory,
      ask: setDialog,
    };
  }, [data, query.isError, isStaff, owedItems]);

  return (
    <OwnersContext.Provider value={value}>
      {children}
      <ItemDialog dialog={dialog} close={() => setDialog(null)} />
      {history && <ItemHistory item={history} close={() => setHistory(null)} />}
    </OwnersContext.Provider>
  );
};

// ------------------------------------------------------------------ //
// The owner chip and its picker
// ------------------------------------------------------------------ //

/** The people on the list, then InRange, then Clear: one item's picker and the bulk bar's.
 * ``current`` checks the item's owner; undefined for several items, which have no one. */
export const OwnerMenu = ({
  current,
  onPick,
  foot,
}: {
  current: Assignee | undefined;
  onPick: (assignee: Assignee) => void;
  foot: string;
}) => {
  const o = useOwners();
  const count = (id: string) => (o.isStaff ? `${o.openCounts.get(id) ?? 0} open` : undefined);
  return (
    <AssigneeMenu
      sections={[
        (o.owners?.people ?? []).map((p) => ({
          key: p.person_id,
          mark: <PersonMark id={p.person_id} name={p.name} />,
          label: `${p.name}${p.person_id === o.owners?.me ? " (you)" : ""}`,
          aside: count(p.person_id),
          checked: current === p.person_id,
          onPick: () => onPick(p.person_id),
        })),
        [
          {
            key: INRANGE,
            mark: <PersonMark id={INRANGE} name="InRange" />,
            label: (
              <>
                InRange
                <span className={styles.muted}>Not an Amazon ask</span>
              </>
            ),
            aside: count(INRANGE),
            checked: current === INRANGE,
            onPick: () => onPick(INRANGE),
          },
        ],
      ]}
      onClear={current !== null ? () => onPick(null) : null}
      foot={foot}
    />
  );
};

export const OwnerChip = ({ item }: { item: ItemRef }) => {
  const o = useOwners();
  const readOnly = useReadOnly();
  const assign = useAssignItem();
  if (!o.owners) return o.failed ? <span className={styles.muted}>Owner not loaded</span> : null;
  const current = o.ownerOf(item);
  // While Amazon's answer waits, the owner moves only through InRange's decision.
  const held = waitsOnInRange(o.answerOf(item));
  const label = current ? o.nameOf(current) : "Assign";
  return (
    <AssigneeChip
      label={label}
      mark={<PersonMark id={current} name={label} />}
      assigned={current !== null}
      ariaLabel={current ? `Assigned to ${label}, change` : "Assign an owner"}
      title={readOnly ?? (held ? "Waiting on InRange's decision" : undefined)}
      disabled={!!readOnly || held || assign.isLoading}
      error={assign.isError ? assign.error.message : null}
      menu={(close) => (
        <OwnerMenu
          current={current}
          onPick={(assignee) => {
            close();
            if (assignee === current) return;
            if (assignee === null) return o.ask({ kind: "clear", item, expected: current });
            if (assignee === INRANGE) return o.ask({ kind: "inrange", item, expected: current });
            assign.mutate({
              project_id: item.projectId,
              item_key: item.itemKey,
              assignee,
              expected: current,
            });
          }}
          foot="Recorded in the item's history and posted to Slack."
        />
      )}
    />
  );
};

// ------------------------------------------------------------------ //
// Amazon's answer on the item's line, and its actions
// ------------------------------------------------------------------ //

const eventLine = (a: ItemAnswer, e: ItemAnswer["events"][number]): string => {
  const said = e.text ? `: “${e.text}”` : ".";
  if (e.kind === "submitted")
    return a.kind === "resolved"
      ? `${e.name} said on ${e.when}${said}`
      : `${e.name} assigned it to InRange on ${e.when}${said}`;
  if (e.kind === "asked") return `${e.name} asked on ${e.when}${said}`;
  if (e.kind === "approved") return `${e.name} approved it on ${e.when}${said}`;
  if (e.kind === "dismissed") return `${e.name} dismissed it on ${e.when}.`;
  return `${e.name} replied on ${e.when}${said}`;
};

export const AnswerBlock = ({ item }: { item: ItemRef }) => {
  const o = useOwners();
  const readOnly = useReadOnly();
  const dismiss = useDismissAnswer();
  const a = o.answerOf(item);
  if (!a || !answerShows(a)) return null;
  const amazon = !o.isStaff;
  const back = a.previous ?? "nobody";
  const ending =
    a.status === "pending"
      ? "Pending InRange approval."
      : a.status === "asked"
        ? amazon
          ? "InRange asked for more. Reply to send it back."
          : "Waiting for Amazon's reply."
        : a.kind === "resolved"
          ? amazon
            ? "InRange has not approved it as received. If you still believe it was given, dismiss and mark it as received again."
            : "Not approved. Still needed."
          : amazon
            ? `InRange has not approved the assignment to InRange, so it is back with ${back}. If you still believe this is not an Amazon ask, dismiss and assign it to InRange again.`
            : `Not approved. Back with ${back}.`;
  return (
    <div className={`${styles.answer} ${a.status === "declined" ? styles.declined : ""}`}>
      <div className={styles.answerKind}>
        {a.kind === "resolved" ? "Marked as received" : "Assigned to InRange"}
      </div>
      {a.events
        .filter((e) => e.kind !== "dismissed" && e.kind !== "not_recorded")
        .map((e) => (
          <div key={e.at + e.kind}>{eventLine(a, e)}</div>
        ))}
      <div className={styles.answerState}>{ending}</div>
      {amazon && (a.status === "asked" || a.status === "declined") && (
        <div className={styles.answerActions}>
          {a.status === "asked" && (
            <button
              type="button"
              className={styles.solid}
              disabled={!!readOnly}
              title={readOnly ?? undefined}
              onClick={() => o.ask({ kind: "reply", item, answerId: a.answer_id })}
            >
              Reply
            </button>
          )}
          {a.status === "declined" && (
            <button
              type="button"
              className={styles.ghost}
              disabled={!!readOnly || dismiss.isLoading}
              title={readOnly ?? undefined}
              onClick={() =>
                dismiss.mutate({
                  project_id: item.projectId,
                  item_key: item.itemKey,
                  answer_id: a.answer_id,
                })
              }
            >
              Dismiss
            </button>
          )}
        </div>
      )}
      {dismiss.isError && <div className={styles.error}>{dismiss.error.message}</div>}
    </div>
  );
};

/** Mark as received, for Amazon on an item still owed with nothing waiting, and History. */
export const ItemActions = ({ item, layout }: { item: ItemRef; layout: "column" | "row" }) => {
  const o = useOwners();
  const readOnly = useReadOnly();
  const answer = o.answerOf(item);
  const canAnswer = !o.isStaff && item.owed && !answerShows(answer) && !approvedResolved(answer);
  return (
    <div className={`${styles.actions} ${layout === "row" ? styles.actionsRow : ""}`}>
      {canAnswer && (
        <button
          type="button"
          className={styles.solid}
          disabled={!!readOnly}
          title={readOnly ?? undefined}
          onClick={() => o.ask({ kind: "resolved", item })}
        >
          Mark as received
        </button>
      )}
      <button type="button" className={styles.ghost} onClick={() => o.openHistory(item)}>
        History
      </button>
    </div>
  );
};

// ------------------------------------------------------------------ //
// Confirmations
// ------------------------------------------------------------------ //

const short = (text: string): string => (text.length > 80 ? `${text.slice(0, 78)}…` : text);

const ItemDialog = ({ dialog, close }: { dialog: Dialog | null; close: () => void }) => {
  const assign = useAssignItem();
  const resolve = useMarkResolved();
  const reply = useReplyToInRange();
  const busy = assign.isLoading || resolve.isLoading || reply.isLoading;
  const error = (assign.error ?? resolve.error ?? reply.error)?.message ?? null;
  const done = useCallback(() => {
    assign.reset();
    resolve.reset();
    reply.reset();
    close();
  }, [assign, resolve, reply, close]);
  if (!dialog) return <NoteDialog {...CLOSED} />;
  const { item } = dialog;
  const where = `“${short(item.text)}” on ${item.siteCode}`;
  const base = { open: true, isSubmitting: busy, itemName: item.text, onCancel: done, submitError: error };
  const ids = { project_id: item.projectId, item_key: item.itemKey };
  if (dialog.kind === "clear")
    return (
      <NoteDialog
        {...base}
        heading="Clear the assignment?"
        helpText={`${where} will not be assigned to anyone. It moves to Unassigned.`}
        placeholder=""
        submitLabel="Clear assignment"
        withNote={false}
        tone="danger"
        onSubmit={() =>
          assign.mutate({ ...ids, assignee: null, expected: dialog.expected }, { onSuccess: done })
        }
      />
    );
  if (dialog.kind === "inrange")
    return (
      <NoteDialog
        {...base}
        heading="Are you sure this is not an Amazon ask?"
        helpText={`This list is what Amazon owes. Assigning ${where} to InRange says Amazon does not owe it. It shows as pending InRange approval until InRange approves; if InRange does not, it goes back to whoever had it.`}
        fieldLabel="Why do you believe it is not an Amazon ask?"
        placeholder="For example, who holds it"
        submitLabel="Assign to InRange"
        optional
        onSubmit={(why) =>
          assign.mutate(
            { ...ids, assignee: INRANGE, expected: dialog.expected, reason: why },
            { onSuccess: done },
          )
        }
      />
    );
  if (dialog.kind === "resolved")
    return (
      <NoteDialog
        {...base}
        heading="Mark as received?"
        helpText="Tell InRange how it was given. It shows as pending InRange approval until InRange approves."
        fieldLabel="How was it given? For example, when and how it was sent."
        requiredMessage="Please say how, so InRange can check it."
        placeholder="Sent with the network diagram to noaa@inrange.io on 12 Oct"
        submitLabel="Send to InRange"
        onSubmit={(text) => resolve.mutate({ ...ids, text }, { onSuccess: done })}
      />
    );
  return (
    <NoteDialog
      {...base}
      heading="Reply to InRange"
      helpText={`Your reply goes back to InRange with ${where}, pending their approval.`}
      fieldLabel="Your reply"
      requiredMessage="Please write your reply."
      placeholder=""
      submitLabel="Send reply"
      onSubmit={(text) => reply.mutate({ ...ids, text, answer_id: dialog.answerId }, { onSuccess: done })}
    />
  );
};

const CLOSED = {
  open: false,
  isSubmitting: false,
  heading: "",
  itemName: "",
  helpText: "",
  placeholder: "",
  submitLabel: "",
  onCancel: () => undefined,
  onSubmit: () => undefined,
};

// ------------------------------------------------------------------ //
// History
// ------------------------------------------------------------------ //

const ItemHistory = ({ item, close }: { item: ItemRef; close: () => void }) => {
  const o = useOwners();
  const history = useItemHistory(item.projectId, item.itemKey, true);
  const owner = o.ownerOf(item);
  const cited = useMemo(
    () => (history.data ?? []).flatMap((line) => (line.what_cited ? [line.what_cited] : [])),
    [history.data],
  );
  const citations = useCitations(cited);
  return (
    <aside className={styles.pane} aria-label="Item history">
      <div className={styles.paneHead}>
        <div>
          <div className={styles.muted}>{item.siteCode}</div>
          <div className={styles.paneTitle}>{item.text}</div>
          <div className={styles.muted}>
            {item.owed ? "Not received" : "Received"} ·{" "}
            {owner === null ? "Unassigned" : `Assigned to ${o.nameOf(owner)}`}
          </div>
        </div>
        <button type="button" className={`${styles.ghost} ${styles.close}`}
          onClick={close}
          aria-label="Close history"
        >
          ×
        </button>
      </div>
      {history.isLoading && <p className={styles.muted}>Loading the history…</p>}
      {history.isError && <p className={styles.error}>Could not load the history. Close and try again.</p>}
      <ol className={styles.timeline}>
        {(history.data ?? []).map((line, i) => (
          <li key={i} className={line.inrange_only ? styles.inrangeOnly : undefined}>
            <div className={styles.muted}>
              {line.when} · {line.who}
            </div>
            {line.what_cited ? (
              <CitedBody body={line.what_cited} citations={citations} />
            ) : (
              <div>{line.what}</div>
            )}
            {line.text && <div className={styles.lineText}>&ldquo;{line.text}&rdquo;</div>}
            {line.inrange_only && <div className={styles.muted}>Only InRange sees this.</div>}
          </li>
        ))}
      </ol>
      {history.data?.length === 0 && <p className={styles.muted}>Nothing recorded yet.</p>}
      {citations.modal}
    </aside>
  );
};
